import { t } from "@lingui/core/macro";
import { EMPTY_STRING } from "@regrello/core-utils";
import { isValidPhoneNumber } from "libphonenumber-js/max";
import type React from "react";
import { useCallback } from "react";
import { type FieldPath, type FieldValues, useController } from "react-hook-form";

import { RegrelloFormFieldPhone, type RegrelloFormFieldPhoneProps } from "../RegrelloFormFieldPhone";
import { getErrorProp, type RegrelloControlledFormFieldProps, useInvokeOnChangeOnRequiredStateChange } from "./utils";

/**
 * A controlled phone number field for use with react-hook-form.
 */
export function RegrelloControlledFormFieldPhone<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  controllerProps,
  onValueChange,
  ...phoneFieldProps
}: RegrelloControlledFormFieldProps<
  TFieldValues,
  TName,
  Omit<RegrelloFormFieldPhoneProps, "name" | "onChange" | "value">
> & {
  /**
   * Capture the change event before the rerender happens. This is useful for setting the
   * "useForm" form values to the correct type before rendering a new RegrelloFormField of a
   * different type. This avoids passing in a incorrect data type to the component.
   */
  onValueChange?: (name: string, newValue: string) => void;
}) {
  const { field, fieldState } = useController({
    ...controllerProps,
    rules: {
      ...controllerProps.rules,
      validate: (value: string) => {
        try {
          return value === EMPTY_STRING || isValidPhoneNumber(value) ? true : t`Phone number is invalid`;
        } catch (_error) {
          return t`Phone number is invalid`;
        }
      },
    },
  });
  const { ref, ...fieldProps } = field;

  const internalOnBlur = fieldProps.onBlur;
  const propsOnBlur = phoneFieldProps.onBlur;

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      internalOnBlur();
      propsOnBlur?.(event);
    },
    [internalOnBlur, propsOnBlur],
  );

  useInvokeOnChangeOnRequiredStateChange(field, phoneFieldProps.isRequiredAsteriskShown ?? false);

  return (
    <RegrelloFormFieldPhone
      {...fieldProps}
      {...phoneFieldProps}
      {...getErrorProp(fieldState)}
      dataTestId={phoneFieldProps.dataTestId ?? field.name}
      inputRef={ref}
      onBlur={handleBlur}
      onChange={(value) => {
        onValueChange?.(field.name, value);
        fieldProps.onChange(value);
      }}
      value={fieldProps.value ?? EMPTY_STRING}
    />
  );
}
